import React, { useEffect, useState } from "react";
import Big from "big.js";
import { useDebounce } from "react-use";
import { NumberFormatValues, SourceInfo } from "react-number-format";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import { SInputContainer, SInputField, SLabel } from "./styles";
import { useGlobalConfigs, useInitialApplication } from "../../../store";
import { CurrencyInput } from "..";
import { useSaveUserData } from "../../../api";

export const ApplicationAmount: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [localApplicationAmount, setLocalApplicationAmount] = useState(100);
  const { initialApplication, setInitialApplication } = useInitialApplication();
  const { initialApplication: initialApplicationLimits } = useGlobalConfigs();
  const { savePortfolio } = useSaveUserData();

  const handleChange = (
    maskedInputValue: NumberFormatValues,
    sourceInfo: SourceInfo
  ): void => {
    // onValueChange is called on EVERY change (unlike onChange) not only on events.
    // When value is fully deleted by the user, sourceinfo.source equals to "props" and not "event"
    if (
      sourceInfo.source === "event" ||
      (sourceInfo.source === "prop" && maskedInputValue.floatValue === 0)
    ) {
      setLocalApplicationAmount(maskedInputValue.floatValue || 0);
    }
  };

  const handleUpdateInitialApplication = (): void => {
    if (localApplicationAmount !== initialApplication) {
      if (localApplicationAmount > initialApplicationLimits.max) {
        setLocalApplicationAmount(initialApplicationLimits.max);
        setInitialApplication(initialApplicationLimits.max);
        return;
      }

      if (localApplicationAmount < initialApplicationLimits.min) {
        setLocalApplicationAmount(initialApplicationLimits.min);
        setInitialApplication(initialApplicationLimits.min);
        return;
      }

      setInitialApplication(Big(localApplicationAmount).toNumber());
    }
  };

  const [, cancelInitialApplicationDebounce] = useDebounce(
    () => {
      handleUpdateInitialApplication();
    },
    theme.delay.numberInput,
    [localApplicationAmount]
  );

  const handleEnterPress: React.KeyboardEventHandler = (event): void => {
    if (event.key === "Enter") {
      handleUpdateInitialApplication();
      cancelInitialApplicationDebounce();
    }
  };

  useEffect(() => {
    setLocalApplicationAmount(initialApplication);
  }, [initialApplication]);

  useEffect(() => {
    savePortfolio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialApplication]);

  return (
    <SInputContainer>
      <SLabel>{t("applicationAmount.initialApplication")}</SLabel>
      <CurrencyInput
        onValueChange={handleChange}
        onKeyDown={handleEnterPress}
        value={Big(localApplicationAmount).toNumber()}
        customInput={SInputField}
        disableUnderline
      />
    </SInputContainer>
  );
};
